import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TabPanel } from './inc-page-tab-panels.context';

export type TabNavigationOptions = {
  keepSearchParams?: boolean;
};

type UseTabNavigationProps = {
  tabs: TabPanel[];
  options?: TabNavigationOptions;
};

export const useTabNavigation = ({ tabs, options }: UseTabNavigationProps) => {
  const navigate = useNavigate();
  let location = useLocation();

  const tabPath = location.pathname.split('/').pop();

  // Default to the first tab if no match is found
  const [tabIndex, setTabIndex] = useState<number>(
    tabs.findIndex(tab => tab.path === tabPath) !== -1
      ? tabs.findIndex(tab => tab.path === tabPath)
      : 0, // Fallback to the first tab if no match
  );

  const handleTabChange = (_event: React.SyntheticEvent, newIndex: number) => {
    setTabIndex(newIndex);
    navigate({
      pathname: tabs[newIndex].path,
      search: options?.keepSearchParams ? location.search : undefined,
    });
  };

  useEffect(() => {
    const tabIndex = tabs.findIndex(tab =>
      location.pathname.includes(tab.path),
    );
    if (tabIndex > -1) {
      setTabIndex(tabIndex);
    } else {
      setTabIndex(0); // Fallback to the first tab if no match
    }
  }, [location.pathname, tabs]);

  return {
    tabIndex,
    tabLabel: tabs[tabIndex]?.label,
    tabPath,
    handleTabChange,
  };
};

export const useSetInitialTabAsDefault = (tabs: TabPanel[]) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname.split('/').pop();

  useEffect(() => {
    const isCurrentPathInTabs = tabs.some(tab => tab.path === currentPath);

    if (!isCurrentPathInTabs) {
      navigate(
        {
          pathname: tabs[0]?.path,
          search: window.location.search,
        },
        { replace: true },
      );
    }
  }, [currentPath, navigate, tabs]);
};
